import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, extractApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import StatusPill from '@/components/marketplace/StatusPill';
import { toast } from 'sonner';
import {
  PlusCircle, Trash2, Pencil, Send, Sparkles, TrendingUp, Zap, Flame,
  CircleCheck, Undo2, Info, Star as StarIcon, Gift, Search, X as XIcon,
} from 'lucide-react';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'live', label: 'Live' },
  { key: 'pending_review', label: 'Pending' },
  { key: 'needs_correction', label: 'Needs correction' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'sold', label: 'Sold' },
  { key: 'expired', label: 'Expired' },
];

const EXPIRING_TYPES = ['sell', 'wanted'];

const daysUntil = (iso) => {
  if (!iso) return null;
  const diff = new Date(iso) - new Date();
  return Math.ceil(diff / (24 * 3600 * 1000));
};

// How many days after `sold_at` the seller can self-revert without staff help.
const SELF_REVERT_DAYS = 7;

const isSelfRevertOk = (sold_at) => {
  if (!sold_at) return false;
  const soldDate = new Date(sold_at);
  if (Number.isNaN(soldDate.getTime())) return false;
  const cutoff = Date.now() - SELF_REVERT_DAYS * 24 * 60 * 60 * 1000;
  return soldDate.getTime() >= cutoff;
};

const MyListings = () => {
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boostModal, setBoostModal] = useState({ open: false, listing: null });
  const [boostConfirm, setBoostConfirm] = useState(null); // { plan, quote }
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState('');
  // Redeemable boost credits earned from the Reward Store (points → credit).
  // Loaded once on mount and re-loaded after any successful apply.
  const [boostCredits, setBoostCredits] = useState([]);
  const [applyingCreditId, setApplyingCreditId] = useState('');
  // Mark as Sold dialog state
  const [soldModal, setSoldModal] = useState({ open: false, listing: null, submitting: false });
  const [soldForm, setSoldForm] = useState({
    sold_price: '', sold_via: '', buyer_experience_rating: '', note: '',
  });
  const [revertBusy, setRevertBusy] = useState('');
  const [renewBusy, setRenewBusy] = useState('');
  const navigate = useNavigate();

  const renewListing = useCallback(async (l) => {
    setRenewBusy(l.id);
    try {
      const { data } = await api.post(`/listings/${l.id}/renew`);
      const until = new Date(data.expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
      toast.success(data.status === 'live'
        ? `Renewed free — live until ${until}`
        : `Renewed free until ${until} — your recent edits are awaiting review`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not renew listing');
    } finally {
      setRenewBusy('');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link from expiry notifications: /dashboard/listings (renewal opens
  // only after the 60-day expiry, so no auto-renew on arrival).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('renew')) navigate('/dashboard/listings', { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async (opts = {}) => {
    const { background = false } = opts;
    if (!background) setLoading(true);
    try {
      const { data } = await api.get('/listings/mine');
      setListings(data);
    } finally { if (!background) setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // #10: Re-fetch listings whenever the tab regains focus. Fixes the
  // "approved by admin but still shows In Queue" perception — moderator
  // approvals happen out-of-band, so returning to the tab should show the
  // fresh status without a manual reload. Runs in the BACKGROUND (no loading
  // spinner) so the user's scroll position is preserved on tab return.
  useEffect(() => {
    const onFocus = () => { load({ background: true }); };
    const onVisible = () => { if (document.visibilityState === 'visible') load({ background: true }); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  useEffect(() => {
    api.get('/payments/plans', { params: {} }).then(r => setPlans(r.data.filter(p => ['featured', 'top', 'urgent'].includes(p.kind))));
  }, []);

  const loadCredits = useCallback(async () => {
    try {
      const { data } = await api.get('/referrals/rewards/credits/mine');
      setBoostCredits(Array.isArray(data) ? data : []);
    } catch {
      setBoostCredits([]);
    }
  }, []);

  useEffect(() => { loadCredits(); }, [loadCredits]);

  const applyCredit = async (credit) => {
    const target = boostModal.listing;
    if (!target) return;
    setApplyingCreditId(credit.id);
    try {
      await api.post(`/referrals/rewards/credits/${credit.id}/apply`, { listing_id: target.id });
      const label = credit.kind === 'featured' ? 'Featured' : 'Urgent';
      toast.success(`${label} applied for ${credit.days} days — no charge`);
      closeBoost();
      await Promise.all([load(), loadCredits()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not apply credit');
    } finally {
      setApplyingCreditId('');
    }
  };

  const openBoost = (l) => setBoostModal({ open: true, listing: l });
  const closeBoost = () => { setBoostModal({ open: false, listing: null }); setBoostConfirm(null); };

  // Step 1 of paid boost: fetch the exact price breakdown (base → discount →
  // GST → total → points equivalent) before any money moves.
  const chooseBoost = async (plan) => {
    setBusy(plan.id);
    try {
      const { data } = await api.post('/payments/quote', { plan_id: plan.id });
      setBoostConfirm({ plan, quote: data });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not fetch price'); }
    finally { setBusy(''); }
  };

  const startBoostPoints = async () => {
    const plan = boostConfirm?.plan;
    if (!plan) return;
    setBusy(plan.id);
    try {
      const order = (await api.post('/payments/create-order', {
        plan_id: plan.id, listing_id: boostModal.listing.id, method: 'points',
      })).data;
      if (order.status === 'paid') {
        toast.success('Boost activated with Rewards Points! Invoice is ready in your dashboard.');
        closeBoost(); load();
      } else {
        toast.error('Points payment did not complete');
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Points payment failed'); }
    finally { setBusy(''); }
  };

  const startBoost = async (plan) => {
    setBusy(plan.id);
    try {
      const cfg = (await api.get('/payments/config')).data;
      const order = (await api.post('/payments/create-order', {
        plan_id: plan.id, listing_id: boostModal.listing.id,
        method: cfg.razorpay_enabled ? 'razorpay' : 'manual',
      })).data;
      if (order.method === 'razorpay' && order.razorpay_order_id && cfg.razorpay_key_id) {
        if (!window.Razorpay) {
          await new Promise((r, e) => { const s = document.createElement('script'); s.src = 'https://checkout.razorpay.com/v1/checkout.js'; s.onload = r; s.onerror = e; document.body.appendChild(s); });
        }
        const opts = {
          key: cfg.razorpay_key_id,
          amount: Math.round(order.amount * 100),
          currency: 'INR', name: 'IndusFact', description: order.description, order_id: order.razorpay_order_id,
          handler: async (response) => {
            try {
              await api.post('/payments/verify', {
                order_id: order.id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              });
              toast.success('Boost activated!');
              closeBoost(); load();
            } catch (e) { toast.error(e?.response?.data?.detail || 'Verification failed'); }
          },
          theme: { color: '#0d3b7a' },
        };
        new window.Razorpay(opts).open();
      } else {
        toast.success('Order created. Please complete payment via bank/UPI from Billing.');
        navigate('/dashboard/billing');
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to start boost'); }
    finally { setBusy(''); }
  };

  const searchQ = search.trim().toLowerCase();
  const filtered = listings.filter(l => {
    if (tab !== 'all' && l.status !== tab) return false;
    if (!searchQ) return true;
    return [l.title, l.id, l.city, l.state, l.category_name]
      .filter(Boolean)
      .some(v => String(v).toLowerCase().includes(searchQ));
  });

  // ── Mark as Sold ───────────────────────────────────────────
  const openSoldDialog = (l) => {
    setSoldForm({ sold_price: '', sold_via: '', buyer_experience_rating: '', note: '' });
    setSoldModal({ open: true, listing: l, submitting: false });
  };
  const closeSoldDialog = () => setSoldModal({ open: false, listing: null, submitting: false });

  const submitMarkSold = async () => {
    if (!soldModal.listing) return;
    setSoldModal(s => ({ ...s, submitting: true }));
    const payload = {};
    if (soldForm.sold_price !== '') payload.sold_price = Number(soldForm.sold_price);
    if (soldForm.sold_via) payload.sold_via = soldForm.sold_via;
    if (soldForm.buyer_experience_rating !== '') payload.buyer_experience_rating = Number(soldForm.buyer_experience_rating);
    if (soldForm.note.trim()) payload.note = soldForm.note.trim();
    try {
      const { data } = await api.post(`/listings/${soldModal.listing.id}/mark-sold`, payload);
      const closureWord = data.closure_label === 'fulfilled' ? 'closed as fulfilled' : 'marked as sold';
      toast.success(`Listing ${closureWord}. ${data.inquiries_closed} inquiries auto-closed.`);
      closeSoldDialog();
      load();
    } catch (e) {
      toast.error(extractApiError(e, 'Failed to mark as sold'));
      setSoldModal(s => ({ ...s, submitting: false }));
    }
  };

  const revertSold = async (l) => {
    const selfOk = isSelfRevertOk(l.sold_at);
    const message = selfOk
      ? 'Revert this listing back to Live? Any auto-closed inquiries stay closed.'
      : `Self-revert window (${SELF_REVERT_DAYS} days) has passed. Please contact support.`;
    if (!selfOk) {
      toast.info(message);
      return;
    }
    if (!window.confirm(message)) return;
    setRevertBusy(l.id);
    try {
      await api.post(`/listings/${l.id}/revert-sold`, {});
      toast.success('Listing reverted to Live');
      load();
    } catch (e) {
      toast.error(extractApiError(e, 'Failed to revert'));
    } finally {
      setRevertBusy('');
    }
  };
  // ───────────────────────────────────────────────────────────

  const remove = async (id) => {
    if (!window.confirm('Delete this listing? This cannot be undone.')) return;
    try { await api.delete(`/listings/${id}`); toast.success('Listing deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const submit = async (id) => {
    try { await api.post(`/listings/${id}/submit`); toast.success('Submitted for review'); load(); }
    catch { toast.error('Failed to submit'); }
  };

  return (
    <div className='space-y-5'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-semibold tracking-tight'>My listings</h1>
          <p className='text-muted-foreground text-sm'>{listings.length} total listings</p>
        </div>
        <Link to='/post'><Button data-testid='mylistings-post-cta' className='bg-accent text-accent-foreground hover:bg-accent/90'><PlusCircle className='h-4 w-4 mr-1.5' /> Post listing</Button></Link>
      </div>

      {boostCredits.length > 0 && (
        <div
          className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3'
          data-testid='mylistings-credits-banner'
        >
          <Gift className='h-5 w-5 text-amber-700 shrink-0 mt-0.5' />
          <div className='text-sm text-amber-900'>
            <div className='font-semibold'>
              You have {boostCredits.length} unused reward
              {boostCredits.length === 1 ? '' : 's'} 🎁
            </div>
            <div className='mt-0.5'>
              Click <strong>Boost</strong> on any live listing and pick
              &quot;Apply free reward&quot; — no payment needed.
            </div>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className='flex-wrap h-auto' data-testid='mylistings-tabs'>
          {STATUS_TABS.map(t => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`mylistings-tab-${t.key}`}>
              {t.label} <span className='ml-1 text-[10px] text-muted-foreground'>({t.key === 'all' ? listings.length : listings.filter(l => l.status === t.key).length})</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className='relative mt-3'>
        <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Search your listings — title, city, category, or listing ID'
          className='pl-9'
          data-testid='mylistings-search-input'
        />
        {search && (
          <button
            type='button'
            onClick={() => setSearch('')}
            className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground'
            data-testid='mylistings-search-clear'
            aria-label='Clear search'
          >
            <XIcon className='h-4 w-4' />
          </button>
        )}
      </div>

      {loading ? (
        <Card className='p-10 text-center text-muted-foreground'>Loading…</Card>
      ) : filtered.length === 0 ? (
        <Card className='p-10 text-center'>
          <div className='text-lg font-semibold'>{search ? 'No listings match your search' : 'No listings here yet'}</div>
          <div className='text-sm text-muted-foreground mt-1'>{search ? 'Try a different title, city, or ID.' : 'Start by posting your first listing.'}</div>
          {!search && <Link to='/post'><Button className='mt-4'>Post listing</Button></Link>}
        </Card>
      ) : (
        <div className='space-y-3'>
          {filtered.map(l => (
            <Card key={l.id} className='p-4 flex flex-col md:flex-row gap-4' data-testid='mylistings-row'>
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-2 mb-1 flex-wrap'>
                  <StatusPill status={l.status} />
                  <span className='text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase bg-secondary'>{l.listing_type}</span>
                  {l.category_name && <span className='text-xs text-muted-foreground'>{l.category_name} · {l.subcategory_name}</span>}
                </div>
                <Link to={`/listing/${l.id}`} className='font-semibold hover:underline'>{l.title}</Link>
                <div className='text-xs text-muted-foreground mt-0.5'>
                  {[l.city, l.state].filter(Boolean).join(', ')} · {l.view_count || 0} views · {l.inquiry_count || 0} inquiries · {l.reveal_count || 0} reveals
                </div>
                {EXPIRING_TYPES.includes(l.listing_type) && l.status === 'live' && l.expires_at && (
                  <div className={`text-xs mt-1 ${daysUntil(l.expires_at) <= 7 ? 'text-amber-700 font-medium' : 'text-muted-foreground'}`} data-testid={`mylistings-expiry-${l.id}`}>
                    Expires {new Date(l.expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {daysUntil(l.expires_at) !== null && ` (${Math.max(0, daysUntil(l.expires_at))} days left)`}
                    {daysUntil(l.expires_at) <= 7 && ' — you can renew it free once it expires'}
                  </div>
                )}
                {l.status === 'expired' && !l.sold_archived_at && (
                  <div className='text-xs mt-1 text-red-700' data-testid={`mylistings-expired-note-${l.id}`}>
                    Expired{l.expired_at ? ` on ${new Date(l.expired_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''} — renew free to go live again
                  </div>
                )}
                {l.moderation_log && l.moderation_log.length > 0 && (
                  <div className='text-xs text-muted-foreground mt-1'>
                    Last moderation: <span className='font-medium'>{l.moderation_log[l.moderation_log.length - 1].action}</span>
                    {l.moderation_log[l.moderation_log.length - 1].reason && ` — ${l.moderation_log[l.moderation_log.length - 1].reason}`}
                  </div>
                )}
              </div>
              <div className='flex items-center gap-2 shrink-0 flex-wrap'>
                {EXPIRING_TYPES.includes(l.listing_type) && !l.sold_archived_at && (l.status === 'expired' || (l.status === 'pending_review' && l.expires_at && daysUntil(l.expires_at) <= 0)) && (
                  <Button
                    size='sm'
                    onClick={() => renewListing(l)}
                    disabled={renewBusy === l.id}
                    data-testid={`mylistings-renew-${l.id}`}
                    className='bg-emerald-700 hover:bg-emerald-800 text-white'
                  >
                    <Undo2 className='h-3.5 w-3.5 mr-1' /> {renewBusy === l.id ? 'Renewing…' : 'Renew free'}
                  </Button>
                )}
                {(l.status === 'live' || l.status === 'approved') && (
                  <Button size='sm' variant='outline' onClick={() => openBoost(l)} data-testid={`mylistings-boost-${l.id}`} className='border-accent text-accent hover:bg-accent hover:text-accent-foreground'>
                    <Sparkles className='h-3.5 w-3.5 mr-1' /> {l.is_featured || l.is_top || l.is_urgent ? 'Boosted' : 'Boost'}
                  </Button>
                )}
                {(l.status === 'live' || l.status === 'approved') && (l.listing_type === 'sell' || l.listing_type === 'wanted') && (
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => openSoldDialog(l)}
                    data-testid={`mylistings-mark-sold-${l.id}`}
                    className='border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white'
                  >
                    <CircleCheck className='h-3.5 w-3.5 mr-1' />
                    {l.listing_type === 'wanted' ? 'Mark fulfilled' : 'Mark as sold'}
                  </Button>
                )}
                {l.status === 'sold' && (
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => revertSold(l)}
                    disabled={revertBusy === l.id}
                    data-testid={`mylistings-revert-sold-${l.id}`}
                    className={isSelfRevertOk(l.sold_at) ? '' : 'opacity-60'}
                    title={isSelfRevertOk(l.sold_at) ? 'Revert to Live' : `Self-revert window (${SELF_REVERT_DAYS}d) has passed — contact support`}
                  >
                    <Undo2 className='h-3.5 w-3.5 mr-1' />
                    {revertBusy === l.id ? 'Reverting…' : 'Revert'}
                  </Button>
                )}
                {(l.status === 'draft' || l.status === 'needs_correction') && (
                  <Button size='sm' variant='outline' onClick={() => submit(l.id)}><Send className='h-3.5 w-3.5 mr-1' /> Submit</Button>
                )}
                <Link to={`/post/${l.id}/edit`}><Button size='sm' variant='outline'><Pencil className='h-3.5 w-3.5 mr-1' /> Edit</Button></Link>
                <Button size='sm' variant='outline' onClick={() => remove(l.id)} data-testid={`mylistings-delete-${l.id}`}>
                  <Trash2 className='h-3.5 w-3.5 mr-1' /> Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={boostModal.open} onOpenChange={(o) => !o && closeBoost()}>
        <DialogContent className='max-h-[85vh] overflow-y-auto max-w-lg' data-testid='boost-dialog'>
          <DialogHeader>
            <DialogTitle>Boost this listing</DialogTitle>
            <DialogDescription>
              Apply an earned reward for free, or choose a paid boost to
              lift this listing above the rest.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            <div className='text-sm text-muted-foreground'>Apply a boost to <strong>{boostModal.listing?.title}</strong> for more visibility.</div>

            {boostCredits.length > 0 && (
              <div className='space-y-2' data-testid='boost-credits-section'>
                <div className='text-xs font-semibold uppercase tracking-wide text-amber-900 flex items-center gap-1.5'>
                  <Gift className='h-3.5 w-3.5' /> Apply your earned rewards
                </div>
                <div className='grid gap-2'>
                  {boostCredits.map(c => {
                    const isFeatured = c.kind === 'featured';
                    const Icon = isFeatured ? Zap : Flame;
                    const label = isFeatured ? `Feature Ad — ${c.days} days` : `Urgent Tag — ${c.days} days`;
                    return (
                      <div
                        key={c.id}
                        className='flex items-center justify-between border border-amber-300 bg-amber-50 rounded-lg p-3'
                        data-testid={`boost-credit-${c.id}`}
                      >
                        <div>
                          <div className='flex items-center gap-2'>
                            <Icon className='h-4 w-4 text-amber-700' />
                            <span className='font-semibold text-amber-900'>{label}</span>
                          </div>
                          <div className='text-xs text-amber-800/80 mt-0.5'>Earned via reward store</div>
                        </div>
                        <Button
                          size='sm'
                          onClick={() => applyCredit(c)}
                          disabled={applyingCreditId === c.id || !!busy}
                          className='bg-amber-600 hover:bg-amber-700 text-white'
                          data-testid={`boost-credit-apply-${c.id}`}
                        >
                          {applyingCreditId === c.id ? 'Applying…' : 'Apply free'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
                <div className='h-px bg-border my-2' />
                <div className='text-[11px] text-muted-foreground'>Or pay for a different boost below:</div>
              </div>
            )}

            {boostConfirm ? (
              <div className='rounded-lg border p-4 space-y-3' data-testid='boost-confirm-panel'>
                <div className='font-semibold'>{boostConfirm.plan.name}</div>
                <div className='text-sm space-y-1'>
                  <div className='flex justify-between'><span>Base price</span><span className='tabular-nums'>₹ {Number(boostConfirm.quote.base_price).toFixed(2)}</span></div>
                  {boostConfirm.quote.discount > 0 && (
                    <div className='flex justify-between text-[hsl(var(--verified))]'>
                      <span>Discount{boostConfirm.quote.promotion_name ? ` (${boostConfirm.quote.promotion_name})` : ''}</span>
                      <span className='tabular-nums'>− ₹ {Number(boostConfirm.quote.discount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className='flex justify-between'><span>Taxable amount</span><span className='tabular-nums'>₹ {Number(boostConfirm.quote.taxable_amount).toFixed(2)}</span></div>
                  <div className='flex justify-between'><span>GST ({boostConfirm.quote.gst_rate_pct}%)</span><span className='tabular-nums'>₹ {Number(boostConfirm.quote.total_tax).toFixed(2)}</span></div>
                  <div className='flex justify-between font-semibold border-t pt-1' data-testid='boost-confirm-total'>
                    <span>Total payable</span><span className='tabular-nums'>₹ {Number(boostConfirm.quote.grand_total).toFixed(2)}</span>
                  </div>
                </div>
                <div className='text-xs text-muted-foreground'>
                  Rewards Points: {Number(boostConfirm.quote.points_required).toLocaleString('en-IN')} pts required · you have {Number(boostConfirm.quote.points_balance).toLocaleString('en-IN')}
                </div>
                <div className='flex flex-col gap-2'>
                  <Button
                    onClick={() => { const p = boostConfirm.plan; setBoostConfirm(null); startBoost(p); }}
                    disabled={!!busy}
                    data-testid='boost-pay-razorpay'
                  >
                    Pay ₹ {Number(boostConfirm.quote.grand_total).toFixed(2)}
                  </Button>
                  <Button
                    variant='outline'
                    onClick={startBoostPoints}
                    disabled={!!busy || !boostConfirm.quote.points_sufficient}
                    data-testid='boost-pay-points'
                  >
                    {boostConfirm.quote.points_sufficient
                      ? `Pay with ${Number(boostConfirm.quote.points_required).toLocaleString('en-IN')} Rewards Points`
                      : 'Insufficient Rewards Points'}
                  </Button>
                  <Button variant='ghost' size='sm' onClick={() => setBoostConfirm(null)} data-testid='boost-confirm-back'>
                    ← Back to boost options
                  </Button>
                </div>
              </div>
            ) : (
            <div className='grid gap-3'>
              {plans.map(p => {
                const Icon = p.kind === 'top' ? TrendingUp : p.kind === 'urgent' ? Flame : Zap;
                return (
                  <div key={p.id} className='flex items-center justify-between border rounded-lg p-3' data-testid={`boost-plan-${p.slug}`}>
                    <div>
                      <div className='flex items-center gap-2'>
                        <Icon className='h-4 w-4 text-accent' />
                        <span className='font-semibold'>{p.name}</span>
                      </div>
                      <div className='text-xs text-muted-foreground mt-1'>{p.description}</div>
                    </div>
                    <div className='flex items-center gap-3'>
                      <div className='text-right'>
                        <div className='text-lg font-semibold tabular-nums'>₹ {Number(p.price_inr).toLocaleString('en-IN')}</div>
                        <div className='text-[10px] text-muted-foreground' data-testid={`boost-gst-note-${p.slug}`}>+ GST as applicable</div>
                      </div>
                      <Button size='sm' onClick={() => chooseBoost(p)} disabled={!!busy} data-testid={`boost-select-${p.slug}`}>
                        {busy === p.id ? 'Preparing…' : 'Choose'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={closeBoost}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Mark as Sold dialog ─────────────────────────────── */}
      <Dialog open={soldModal.open} onOpenChange={(o) => !o && closeSoldDialog()}>
        <DialogContent className='max-h-[90vh] overflow-y-auto max-w-lg' data-testid='mark-sold-dialog'>
          <DialogHeader>
            <DialogTitle>
              {soldModal.listing?.listing_type === 'wanted' ? 'Mark this requirement as fulfilled' : 'Mark this listing as sold'}
            </DialogTitle>
            <DialogDescription>
              <span className='block'>
                <strong className='text-foreground'>{soldModal.listing?.title}</strong>
              </span>
              <span className='mt-2 block'>
                All the info below is optional but helps you (and IndusFact analytics) understand what closed the deal. Every field can be skipped.
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4'>
            {/* Revert-window info banner */}
            <div className='flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-900 p-3 text-xs'>
              <Info className='h-4 w-4 mt-0.5 text-blue-600 shrink-0' />
              <div>
                You can <strong>revert this to Live</strong> yourself within the next <strong>{SELF_REVERT_DAYS} days</strong>.
                After that, only IndusFact support can revert it — and only with a justifiable reason.
                Any open inquiries on this listing will be auto-closed and the inquirers emailed.
              </div>
            </div>

            {soldModal.listing?.listing_type === 'sell' && (
              <div>
                <Label className='text-xs mb-1 block'>Sold price (₹) <span className='text-muted-foreground'>— optional</span></Label>
                <Input
                  type='number'
                  min='0'
                  value={soldForm.sold_price}
                  onChange={(e) => setSoldForm(f => ({ ...f, sold_price: e.target.value }))}
                  placeholder='e.g. 450000'
                  data-testid='sold-price-input'
                />
              </div>
            )}

            <div>
              <Label className='text-xs mb-1 block'>How did you find the buyer? <span className='text-muted-foreground'>— optional</span></Label>
              <Select
                value={soldForm.sold_via}
                onValueChange={(v) => setSoldForm(f => ({ ...f, sold_via: v === '__none__' ? '' : v }))}
              >
                <SelectTrigger data-testid='sold-via-select'><SelectValue placeholder='Select' /></SelectTrigger>
                <SelectContent>
                  <SelectItem value='__none__'>Prefer not to say</SelectItem>
                  <SelectItem value='indusfact'>Buyer found via IndusFact</SelectItem>
                  <SelectItem value='offline'>Offline / in-person</SelectItem>
                  <SelectItem value='other_platform'>Another platform</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className='text-xs mb-1 block'>Rate your buying/selling experience <span className='text-muted-foreground'>— optional, private</span></Label>
              <div className='flex items-center gap-1' data-testid='sold-rating'>
                {[1, 2, 3, 4, 5].map((n) => {
                  const filled = Number(soldForm.buyer_experience_rating) >= n;
                  return (
                    <button
                      key={n}
                      type='button'
                      onClick={() => setSoldForm(f => ({ ...f, buyer_experience_rating: String(n) }))}
                      className='p-1 rounded hover:bg-secondary'
                      title={`${n} star${n > 1 ? 's' : ''}`}
                      data-testid={`sold-rating-${n}`}
                    >
                      <StarIcon className={`h-5 w-5 ${filled ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground'}`} />
                    </button>
                  );
                })}
                {soldForm.buyer_experience_rating && (
                  <button
                    type='button'
                    onClick={() => setSoldForm(f => ({ ...f, buyer_experience_rating: '' }))}
                    className='ml-2 text-[11px] text-muted-foreground underline'
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div>
              <Label className='text-xs mb-1 block'>Notes for your own records <span className='text-muted-foreground'>— optional, admin-visible</span></Label>
              <Textarea
                rows={3}
                maxLength={500}
                value={soldForm.note}
                onChange={(e) => setSoldForm(f => ({ ...f, note: e.target.value }))}
                placeholder='e.g. Buyer picked up from Faridabad on 14 Feb. Included transport.'
                data-testid='sold-note-input'
              />
            </div>
          </div>

          <DialogFooter className='gap-2'>
            <Button variant='outline' onClick={closeSoldDialog} disabled={soldModal.submitting}>Cancel</Button>
            <Button
              onClick={submitMarkSold}
              disabled={soldModal.submitting}
              data-testid='mark-sold-confirm'
              className='bg-emerald-600 hover:bg-emerald-700 text-white'
            >
              <CircleCheck className='h-4 w-4 mr-1.5' />
              {soldModal.submitting
                ? 'Saving…'
                : (soldModal.listing?.listing_type === 'wanted' ? 'Confirm — mark fulfilled' : 'Confirm — mark as sold')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyListings;
